import { Component, inject } from '@angular/core';
import { PwaService } from '../../../core/services/pwa.service';
import { HlmButton } from '@spartan-ng/helm/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideDownload, lucideX, lucideShare } from '@ng-icons/lucide';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'app-pwa-install-banner',
  standalone: true,
  imports: [HlmButton, NgIcon, TranslocoDirective],
  viewProviders: [provideIcons({ lucideDownload, lucideX, lucideShare })],
  template: `
    <ng-container *transloco="let t">
      @if (pwaService.canInstall()) {
        <div class="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up">
          <div
            class="mx-auto max-w-lg border-t border-border bg-background px-4 py-3 shadow-lg sm:mb-4 sm:rounded-xl sm:border sm:mx-4 md:mx-auto"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10"
              >
                <ng-icon name="lucideDownload" class="text-primary text-lg" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-foreground">
                  {{ t('pwa.install.title') }}
                </p>
                <p class="text-xs text-muted-foreground">
                  {{ t('pwa.install.subtitle') }}
                </p>
              </div>
              <div class="flex shrink-0 items-center gap-1.5">
                <button
                  hlmBtn
                  size="sm"
                  variant="default"
                  (click)="install()"
                >
                  {{ t('pwa.install.installButton') }}
                </button>
                <button
                  hlmBtn
                  size="icon-sm"
                  variant="ghost"
                  (click)="dismiss()"
                >
                  <ng-icon name="lucideX" class="text-sm" />
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      @if (pwaService.showIosInstallHint()) {
        <div class="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up">
          <div
            class="mx-auto max-w-lg border-t border-border bg-background px-4 py-3 shadow-lg sm:mb-4 sm:rounded-xl sm:border sm:mx-4 md:mx-auto"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10"
              >
                <ng-icon name="lucideShare" class="text-primary text-lg" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-foreground">
                  {{ t('pwa.install.iosTitle') }}
                </p>
                <p class="text-xs text-muted-foreground">
                  {{ t('pwa.install.iosInstructions') }}
                </p>
              </div>
              <button
                hlmBtn
                size="icon-sm"
                variant="ghost"
                class="shrink-0"
                (click)="dismiss()"
              >
                <ng-icon name="lucideX" class="text-sm" />
              </button>
            </div>
          </div>
        </div>
      }
    </ng-container>
  `,
  styles: `
    @keyframes slide-up {
      from {
        transform: translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .animate-slide-up {
      animation: slide-up 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    }
  `,
})
export class PwaInstallBannerComponent {
  readonly pwaService = inject(PwaService);

  async install(): Promise<void> {
    await this.pwaService.promptInstall();
  }

  dismiss(): void {
    this.pwaService.dismissInstall();
  }
}
